import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Switch,
  Button,
  Upload,
  Progress,
  Radio,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import type { UploadFile } from 'antd/es/upload/interface';
// Api
import {
  CREATE_TRANSITION,
  UPDATE_TRANSITION,
  INIT_TRANSITION_MULTIPART_UPLOAD,
  COMPLETE_TRANSITION_MULTIPART_UPLOAD,
} from 'api/video-merge-job/mutations';
// Types
import { GetTransitions_getTransitions } from 'api/video-merge-job/types/GetTransitions';
import {
  InitTransitionMultipartUpload,
  InitTransitionMultipartUploadVariables,
} from 'api/video-merge-job/types/InitTransitionMultipartUpload';
import {
  CompleteTransitionMultipartUpload,
  CompleteTransitionMultipartUploadVariables,
} from 'api/video-merge-job/types/CompleteTransitionMultipartUpload';
import {
  CompleteMultipartUploadInput,
  TransitionType,
} from 'api/graphql-global-types';
// Helpers
import { calculateChunkSize } from 'helpers/mediaFeed';
import { Uploader } from 'helpers/uploader';
import type { AdminInitPostVideoMultipartUploadV2_adminInitPostVideoMultipartUploadV2 } from 'api/mediaPost/types/AdminInitPostVideoMultipartUploadV2';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './CreateEditTransitionModal.module.scss';

const { Dragger } = Upload;

interface CreateEditTransitionModalProps {
  isVisible: boolean;
  isEdit: boolean;
  record: GetTransitions_getTransitions | null;
  onClose: () => void;
  refetchTransitions: () => void;
}

const CreateEditTransitionModal: React.FC<CreateEditTransitionModalProps> = ({
  isVisible,
  isEdit,
  record,
  onClose,
  refetchTransitions,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);

  const [createTransition, { loading: createLoading }] =
    useMutation(CREATE_TRANSITION);
  const [updateTransition, { loading: updateLoading }] =
    useMutation(UPDATE_TRANSITION);
  const [initMultipartUpload] = useMutation<
    InitTransitionMultipartUpload,
    InitTransitionMultipartUploadVariables
  >(INIT_TRANSITION_MULTIPART_UPLOAD);
  const [completeMultipartUpload] = useMutation<
    CompleteTransitionMultipartUpload,
    CompleteTransitionMultipartUploadVariables
  >(COMPLETE_TRANSITION_MULTIPART_UPLOAD);

  useEffect(() => {
    if (isVisible && record && isEdit) {
      form.setFieldsValue({
        name: record.name,
        type: record.type,
        isActive: record.isActive,
      });
      // Set video preview URL for edit mode - use videoURL from backend
      setVideoPreviewUrl(record.videoURL || null);
      setFileList([]);
    } else if (isVisible && !isEdit) {
      form.setFieldsValue({
        name: '',
        type: TransitionType.MiddleOfVideo,
        isActive: true,
      });
      setVideoPreviewUrl(null);
      setFileList([]);
    }
    setUploadProgress(0);
    setIsUploading(false);
  }, [isVisible, record, isEdit, form]);

  const handleFileChange = (info: any) => {
    const { fileList: newFileList } = info;
    setFileList(newFileList);

    // Validate MP4 format
    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj as File;
      if (file) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (fileExtension !== 'mp4') {
          errorNotification('Only MP4 files are allowed');
          setFileList([]);
          return;
        }
        if (!file.type.includes('video')) {
          errorNotification('Please upload a valid video file');
          setFileList([]);
          return;
        }
      }
    }
  };

  const beforeUpload = (file: File): boolean => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension !== 'mp4') {
      errorNotification('Only MP4 files are allowed');
      return false;
    }
    if (!file.type.includes('video')) {
      errorNotification('Please upload a valid video file');
      return false;
    }
    return true;
  };

  const handleUpload = async (
    file: File,
    transitionName: string
  ): Promise<string> => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const chunkSize = calculateChunkSize(file.size);
      const contentType = file.type || 'video/mp4';
      const numberOfParts = Math.ceil(file.size / chunkSize);
      const fileExtension = 'mp4';

      const { data } = await initMultipartUpload({
        variables: {
          input: {
            contentType,
            fileExt: fileExtension,
            partCnt: numberOfParts,
            name: transitionName,
          },
        },
      });

      if (!data?.initTransitionMultipartUpload) {
        throw new Error('Failed to initialize multipart upload');
      }

      let fileKey: string;

      // Cast to the expected type - the structure is the same
      const initData =
        data.initTransitionMultipartUpload as unknown as AdminInitPostVideoMultipartUploadV2_adminInitPostVideoMultipartUploadV2;

      const uploader = new Uploader({
        chunkSize: chunkSize,
        file: file,
        initData: initData,
      });

      uploader.start();

      return new Promise((resolve, reject) => {
        uploader
          .onProgress(({ percentage }) => {
            setUploadProgress(percentage);
          })
          .onError((error) => {
            setIsUploading(false);
            reject(error);
          })
          .onComplete(async (input: CompleteMultipartUploadInput) => {
            fileKey = input.fileKey;

            try {
              await completeMultipartUpload({
                variables: {
                  input,
                },
              });

              setIsUploading(false);
              setUploadProgress(100);
              resolve(fileKey);
            } catch (error) {
              setIsUploading(false);
              reject(error);
            }
          });
      });
    } catch (error) {
      setIsUploading(false);
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      if (isEdit && record) {
        // Edit mode: update name and isActive (type cannot be changed)
        const values = await form.validateFields(['name', 'isActive']);
        await updateTransition({
          variables: {
            id: record.id,
            input: {
              name: values.name,
              isActive: values.isActive,
            },
          },
        });
        successNotification('Transition updated successfully');
        refetchTransitions();
        onClose();
      } else {
        // Create mode: file upload is required
        if (fileList.length === 0 || !fileList[0].originFileObj) {
          errorNotification('Please upload a video file');
          return;
        }

        // Validate and get all form values (create mode only)
        const validatedValues = await form.validateFields([
          'name',
          'type',
          'isActive',
        ]);
        const allFormValues = form.getFieldsValue();

        // Use validated values, but fallback to allFormValues for type if not in validated
        // This ensures we capture the Radio.Group selection
        const transitionName = validatedValues.name || allFormValues.name;
        const transitionType =
          validatedValues.type ??
          allFormValues.type ??
          TransitionType.MiddleOfVideo;
        const transitionIsActive =
          validatedValues.isActive ?? allFormValues.isActive ?? true;

        // Upload file and get the generated key (key is generated from name)
        const uploadedFileKey = await handleUpload(
          fileList[0].originFileObj as File,
          transitionName
        );

        if (!uploadedFileKey) {
          errorNotification('Failed to upload video file');
          return;
        }

        // Use the generated key to create the transition
        await createTransition({
          variables: {
            input: {
              name: transitionName,
              videoKey: uploadedFileKey,
              type: transitionType,
              isActive: transitionIsActive,
            },
          },
        });
        successNotification('Transition created successfully');
        refetchTransitions();
        onClose();
      }
    } catch (error) {
      if (error instanceof Error) {
        // Check for specific error messages
        if (error.message.includes('Only one outro transition can be active')) {
          errorNotification(
            'Only one outro transition can be active at a time'
          );
        } else {
          errorNotification(error.message);
        }
      } else {
        errorNotification('An error occurred');
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setVideoPreviewUrl(null);
    setUploadProgress(0);
    setIsUploading(false);
    onClose();
  };

  return (
    <Modal
      title={isEdit ? 'Edit Transition' : 'Create Transition'}
      visible={isVisible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isUploading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={createLoading || updateLoading || isUploading}
          onClick={handleSubmit}
          disabled={isUploading}
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>,
      ]}
      destroyOnClose
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        name="transitionForm"
        autoComplete="off"
      >
        {isEdit && videoPreviewUrl && (
          <Form.Item label="Video Preview">
            <div className={styles.videoPreview}>
              <video
                src={videoPreviewUrl}
                controls
                onError={() => {
                  errorNotification('Failed to load video preview');
                }}
              >
                <track kind="captions" />
              </video>
            </div>
          </Form.Item>
        )}

        <Form.Item
          name="name"
          label="Transition Name"
          rules={[
            { required: true, message: 'Please enter transition name' },
            { whitespace: true, message: 'Transition name cannot be empty' },
          ]}
        >
          <Input placeholder="Enter transition name" disabled={isUploading} />
        </Form.Item>

        {!isEdit && (
          <Form.Item
            name="type"
            label="Transition Type"
            rules={[
              { required: true, message: 'Please select transition type' },
            ]}
          >
            <Radio.Group
              disabled={isUploading}
              onChange={(e) => {
                form.setFieldsValue({ type: e.target.value });
              }}
            >
              <Radio value={TransitionType.MiddleOfVideo}>
                {TransitionType.MiddleOfVideo}
              </Radio>
              <Radio value={TransitionType.Outro}>{TransitionType.Outro}</Radio>
            </Radio.Group>
          </Form.Item>
        )}

        {!isEdit && (
          <Form.Item label="Upload Video File (MP4 only)" required>
            <Dragger
              fileList={fileList}
              onChange={handleFileChange}
              beforeUpload={beforeUpload}
              accept="video/mp4"
              maxCount={1}
              disabled={isUploading}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">Only MP4 format is supported</p>
            </Dragger>
            {isUploading && (
              <div className={styles.uploadProgress}>
                <Progress
                  percent={Math.round(uploadProgress)}
                  status="active"
                />
              </div>
            )}
          </Form.Item>
        )}

        <Form.Item name="isActive" label="Is Active" valuePropName="checked">
          <Switch disabled={isUploading} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateEditTransitionModal;
